import React, { useState } from 'react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'privat',
    message: '',
    website_url: '', // Honeypot field for antispam
  })

  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: formData.service,
          message: formData.message,
          website_url: formData.website_url, // mandatory honeypot
        }),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: 'privat',
          message: '',
          website_url: '',
        })
      } else {
        const data = await response.json().catch(() => ({}))
        setStatus('error')
        setErrorMessage(
          data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring til mig.'
        )
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage('Kunne ikke oprette forbindelse. Kontrollér dit netværk og prøv igen.')
    }
  }

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-[#D9E3DC] shadow-sm">
      <h3 className="text-xl sm:text-2xl font-bold text-[#285F41] mb-2">
        Send mig en besked
      </h3>
      <p className="text-sm text-[#4A5D50] mb-6">
        Udfyld formularen herunder, så vender jeg hurtigt tilbage til dig.
      </p>

      {status === 'success' ? (
        <div className="bg-[#EEF6F0] border border-[#70A584] rounded-xl p-6 text-center space-y-3 animate-fadeIn">
          <div className="w-12 h-12 rounded-full bg-[#285F41] text-white flex items-center justify-center mx-auto text-xl">
            <i className="fa-regular fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-lg font-bold text-[#285F41]">Mange tak for din henvendelse!</h4>
          <p className="text-sm text-[#3A5243]">
            Jeg har modtaget din besked og kontakter dig hurtigst muligt. Du er også altid velkommen til at ringe direkte på <a href="tel:52506794" className="contact-no-break font-bold underline">52 50 67 94</a>.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-4 px-4 py-2 bg-[#285F41] text-white text-xs font-semibold rounded-lg hover:bg-[#357753] transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Honeypot Field - STRICTLY HIDDEN */}
          <div className="sr-only absolute pointer-events-none opacity-0 -z-10" aria-hidden="true">
            <label htmlFor="website_url">Honeypot Web</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              value={formData.website_url}
              onChange={handleChange}
              autoComplete="off"
            />
          </div>

          {/* Navn */}
          <div>
            <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-[#285F41] mb-1.5">
              Navn <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className="w-full px-4 py-3 rounded-xl border border-[#C5D5C9] bg-[#FAFBF9] text-[#285F41] placeholder-[#8EA596] text-sm focus:outline-none focus:ring-2 focus:ring-[#285F41] focus:border-transparent transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Telefonnummer */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-[#285F41] mb-1.5">
                Telefonnummer <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 52 50 67 94"
                className="w-full px-4 py-3 rounded-xl border border-[#C5D5C9] bg-[#FAFBF9] text-[#285F41] placeholder-[#8EA596] text-sm focus:outline-none focus:ring-2 focus:ring-[#285F41] focus:border-transparent transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#285F41] mb-1.5">
                E-mailadresse <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3 rounded-xl border border-[#C5D5C9] bg-[#FAFBF9] text-[#285F41] placeholder-[#8EA596] text-sm focus:outline-none focus:ring-2 focus:ring-[#285F41] focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Type rengøring */}
          <div>
            <label htmlFor="service" className="block text-xs font-bold uppercase tracking-wider text-[#285F41] mb-1.5">
              Hvad omhandler din henvendelse?
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-[#C5D5C9] bg-[#FAFBF9] text-[#285F41] text-sm focus:outline-none focus:ring-2 focus:ring-[#285F41] focus:border-transparent transition-all"
            >
              <option value="privat">Privat rengøring</option>
              <option value="erhverv">Erhvervsrengøring</option>
              <option value="anden">Anden forespørgsel</option>
            </select>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-[#285F41] mb-1.5">
              Beskrivelse <span className="text-red-600">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Beskriv kort dine ønsker til rengøring (f.eks. antal kvm, hyppighed eller særlige opgaver)..."
              className="w-full px-4 py-3 rounded-xl border border-[#C5D5C9] bg-[#FAFBF9] text-[#285F41] placeholder-[#8EA596] text-sm focus:outline-none focus:ring-2 focus:ring-[#285F41] focus:border-transparent transition-all"
            />
          </div>

          {status === 'error' && (
            <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-center gap-2">
              <i className="fa-regular fa-circle-exclamation text-red-600 shrink-0" aria-hidden="true"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full py-3.5 px-6 rounded-xl bg-[#285F41] text-white font-semibold text-sm shadow-md hover:bg-[#357753] active:scale-[0.99] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-regular fa-circle-notch animate-spin text-sm" aria-hidden="true"></i>
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <i className="fa-regular fa-paper-plane text-sm" aria-hidden="true"></i>
                <span>Send forespørgsel</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
